import {
  useAddAlarmTypeGroup,
  useUpdateAlarmTypeGroupDetails,
} from '@hakimo-ui/hakimo/data-access';
import {
  AlarmTypeGroup,
  AlarmTypeGroupRequestDTO,
} from '@hakimo-ui/hakimo/types';
import { eventTracker, toast } from '@hakimo-ui/hakimo/util';
import {
  Alert,
  Button,
  InputField,
  Modal,
  Textarea,
} from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';

interface Props {
  onClose: () => void;
  alarmTypeGroup?: AlarmTypeGroup;
}

export function AddEditAlarmTypeGroup(props: Props) {
  const { onClose, alarmTypeGroup } = props;
  const isEditing = !!alarmTypeGroup;

  const initialFormValue: AlarmTypeGroupRequestDTO = isEditing
    ? {
        name: alarmTypeGroup.name,
        description: alarmTypeGroup.description,
      }
    : {
        name: '',
        description: '',
      };

  const [formValue, setFormValue] =
    useState<AlarmTypeGroupRequestDTO>(initialFormValue);

  const addAlarmTypeGroupMutation = useAddAlarmTypeGroup(() => {
    toast('Alarm Type Group Added', { type: 'success' });
    onClose();
  });

  const updateAlarmTypeGroupMutation = useUpdateAlarmTypeGroupDetails(
    alarmTypeGroup?.alarmTypeGroupId || 0,
    () => {
      toast('Alarm Type Group Details Updated', { type: 'success' });
      onClose();
    }
  );

  const mutation = isEditing
    ? updateAlarmTypeGroupMutation
    : addAlarmTypeGroupMutation;

  const valuePending = Object.entries(formValue).some(
    ([_, value]: [string, string]) => {
      return value === '';
    }
  );

  const valueUpdater =
    (name: keyof AlarmTypeGroupRequestDTO) =>
    (arg: React.ChangeEvent<HTMLInputElement> | string) => {
      setFormValue((value) => ({
        ...value,
        [name]: typeof arg === 'string' ? arg : arg.target.value,
      }));
    };

  const onSubmit = () => {
    mutation.mutate(formValue);
  };

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={onSubmit}
        disabled={valuePending || mutation.isLoading}
        onSideEffect={eventTracker(
          isEditing
            ? 'submit_update_alarm_type_group_details'
            : 'submit_add_alarm_type_group'
        )}
        loading={mutation.isLoading}
      >
        Submit
      </Button>
      <Button
        onClick={onClose}
        onSideEffect={eventTracker(
          isEditing
            ? 'cancel_update_alarm_type_group_details'
            : 'cancel_add_alarm_type_group'
        )}
      >
        Cancel
      </Button>
    </>
  );

  return (
    <Modal
      title={
        isEditing ? 'Edit Alarm Type Group Details' : 'Add New Alarm Type Group'
      }
      open
      onClose={onClose}
      footer={actions}
    >
      {mutation.isError && (
        <div className="mt-4 px-8">
          <Alert type="error">{mutation.error.message}</Alert>
        </div>
      )}
      <div className="w-[36rem] space-y-4 p-8">
        <InputField
          type="text"
          label="Name"
          value={formValue.name}
          onChange={valueUpdater('name')}
        />
        <Textarea
          rows={2}
          label="Description"
          value={formValue.description}
          onChange={valueUpdater('description')}
        />
      </div>
    </Modal>
  );
}

export default AddEditAlarmTypeGroup;
